/**
 * /non-surgical-face-lift  and  /facial-balancing  (indexable, organic).
 *
 * Built on the same shell as the other additive SEO pages. FAQ text comes from shared/faceLiftFaqs.ts so the
 * visible questions and the FAQPage JSON-LD are the same words. Every dollar figure on the facial balancing
 * page is read from the owner-approved pricing table the ad page uses, so the two can never disagree.
 * The ad pages under /lp/ are untouched and stay noindex.
 */
import { Link } from "wouter";
import { useEffect } from "react";
import { trackViewContent } from "@/lib/gtm";
import TrademarkNote from "@/components/TrademarkNote";
import { FACIAL_BALANCING_FAQS, NON_SURGICAL_FACE_LIFT_FAQS } from "@shared/faceLiftFaqs";
import { FACIAL_BALANCING_PRICING } from "./lp/facialBalancing/pricing";
import { LP_IMAGES } from "./lp/facialBalancing/images";
import { ArticleSection, C, FaqList, PageHero, RelatedLinks, ServicePageShell } from "./AdditiveSeoPages";

const usd = (n: number) => `$${n.toLocaleString("en-US")}`;
const P = FACIAL_BALANCING_PRICING;

function FaqSection({ label, heading, faqs }: { label: string; heading: string; faqs: { q: string; a: string }[] }) {
  return (
    <section aria-label={label} style={{ background: C.warm, border: `1px solid ${C.border}`, borderRadius: 18, margin: "1rem 0 3.5rem", padding: "clamp(1.3rem,4vw,2.4rem)" }}>
      <p style={{ color: C.accent, fontFamily: "'Lato',sans-serif", fontSize: ".74rem", fontWeight: 800, letterSpacing: ".15em", margin: "0 0 .45rem", textTransform: "uppercase" }}>Frequently asked questions</p>
      <h2 style={{ color: C.dark, fontFamily: "'Playfair Display',serif", fontSize: "clamp(1.5rem,3vw,2.1rem)", margin: "0 0 1rem" }}>{heading}</h2>
      <FaqList faqs={faqs.map(({ q, a }) => ({ question: q, answer: a }))} />
    </section>
  );
}

type Option = { concern: string; note: string; links: { label: string; href: string; hint: string }[] };

const OPTIONS: Option[] = [
  {
    concern: "Early looseness in the jawline, lower face, under the chin, or neck",
    note: "The skin itself is relaxing.",
    links: [
      { label: "XERF skin tightening", href: "/xerf-skin-tightening", hint: "Needle-free radiofrequency" },
      { label: "HIFU", href: "/hifu", hint: "Focused ultrasound" },
    ],
  },
  {
    concern: "Jowls, a heavy brow, or a neck that needs lift and support",
    note: "Deeper support is softening.",
    links: [
      { label: "PDO thread lift", href: "/pdo-threads", hint: "Threads for lift" },
      { label: "HIFU", href: "/hifu", hint: "Focused ultrasound" },
    ],
  },
  {
    concern: "Hollow cheeks or temples, a receding chin, a flatter profile",
    note: "Volume and structure are changing.",
    links: [
      { label: "Facial balancing", href: "/facial-balancing", hint: "Areas planned together" },
      { label: "Dermal fillers", href: "/dermal-fillers", hint: "Hyaluronic acid filler" },
    ],
  },
  {
    concern: "Overall thinning and a gradual loss of fullness",
    note: "Collagen is declining.",
    links: [{ label: "Sculptra & Radiesse", href: "/biostimulators", hint: "Collagen biostimulators" }],
  },
  {
    concern: "Lines, a tense jaw, or a brow that sits low",
    note: "Muscle activity is part of the picture.",
    links: [{ label: "Botox, Dysport & Xeomin", href: "/botox", hint: "Neuromodulators" }],
  },
  {
    concern: "Rough texture, enlarged pores, or scarring along with laxity",
    note: "The skin surface needs work too.",
    links: [{ label: "RF microneedling", href: "/rf-microneedling", hint: "Radiofrequency with microneedles" }],
  },
];

export function NonSurgicalFaceLift() {
  useEffect(() => { trackViewContent("non-surgical-face-lift-dallas"); }, []);
  return (
    <ServicePageShell stickyLabel="Non-Surgical Face Lift">
      <PageHero
        eyebrow="Non-surgical face lift in Dallas"
        title="Non-Surgical Face Lift in Dallas, TX"
        intro="A non-surgical face lift is not one treatment. It is a plan that combines the options matching what is actually changing in your face: loose skin, lost volume, softening support, or lines. Dr. Rajan Kohli, MD plans it at Lakewood Med Spa, and the consultation is complimentary."
      />
      <div className="container" style={{ maxWidth: 980 }}>
        <ArticleSection title="What a non-surgical face lift can and cannot do">
          <p>People use the phrase for very different things, so it helps to be specific. These treatments can improve mild-to-moderate looseness, restore volume that has been lost, and soften lines, with no incision and no general anesthesia. They do not remove excess skin or reposition tissue the way a surgical facelift does, and results vary from person to person.</p>
          <p>Where surgery is the better answer, Dr. Kohli will say so at your consultation and can refer you. Where it is not, the aim is a plan that looks like you, planned around your anatomy instead of a package.</p>
        </ArticleSection>

        <ArticleSection title="Match what is changing to the treatment">
          <p>Different changes call for different tools, which is why most good plans combine more than one. Here is how the options line up.</p>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(280px,1fr))", gap: "1rem", marginTop: "1.1rem" }}>
            {OPTIONS.map((o) => (
              <div key={o.concern} style={{ background: C.white, border: `1px solid ${C.border}`, borderRadius: 14, padding: "1.2rem 1.25rem" }}>
                <h3 style={{ color: C.dark, fontFamily: "'Playfair Display',serif", fontSize: "1.12rem", lineHeight: 1.3, margin: "0 0 .35rem" }}>{o.concern}</h3>
                <p style={{ color: C.muted, fontSize: ".9rem", margin: "0 0 .8rem" }}>{o.note}</p>
                <ul style={{ listStyle: "none", margin: 0, padding: 0, display: "grid", gap: ".45rem" }}>
                  {o.links.map((l) => (
                    <li key={l.href + l.label}>
                      <Link href={l.href} style={{ color: C.accent, fontWeight: 800, textDecoration: "none" }}>{l.label}</Link>
                      <span style={{ color: C.muted, fontSize: ".85rem" }}> · {l.hint}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </ArticleSection>

        <ArticleSection title="Why one treatment is rarely the whole answer">
          <p>A face ages in layers. The skin surface, the deeper support beneath it, the fat and bone that give it shape, and the muscles that move it each change in their own way. A treatment that tightens skin will not restore a hollow cheek, and filler will not firm a loose neck. At Lakewood Med Spa, Dr. Kohli looks at which layers are changing in you and chooses accordingly.</p>
          <p>The order and timing of a plan are individualized. Some people need one treatment, some need several, and you will see the plan and its price before you agree to anything.</p>
        </ArticleSection>

        <ArticleSection title="When surgery may be the better answer">
          <p>If you have a significant amount of excess skin, or you want tissue lifted and repositioned, non-surgical treatment is unlikely to give you the result you are picturing. That is not a failure of the treatment. It is a mismatch with the goal, and it is much better to learn that at a consultation than after paying for a series.</p>
        </ArticleSection>

        <ArticleSection title="What to expect at your consultation">
          <p>Dr. Kohli examines your face, asks what you would change, and explains which of the options above fit and which do not. You leave with a written plan and a price. There is no charge for the consultation. Book online or call (214) 247-6793.</p>
        </ArticleSection>

        <ArticleSection title="One Dallas location">
          <p>Lakewood Med Spa is at 8210 Walnut Hill Ln, Professional 1, #516, Dallas, TX 75231, on the Texas Health Presbyterian Hospital campus. Patients come from Dallas, Lakewood, Preston Hollow, Highland Park, University Park, Plano, Frisco, and Richardson. See the <Link href="/areas-we-serve">full service area</Link>.</p>
        </ArticleSection>

        <FaqSection label="Non-surgical face lift frequently asked questions" heading="Non-surgical face lift in Dallas" faqs={NON_SURGICAL_FACE_LIFT_FAQS} />
        <div style={{ margin: "0 0 2.5rem" }}><TrademarkNote brands={["Sculptra"]} /></div>
      </div>
      <RelatedLinks links={[
        { label: "XERF Skin Tightening", href: "/xerf-skin-tightening" },
        { label: "XERF vs Morpheus8, Thermage & Ultherapy", href: "/xerf-vs-morpheus8-thermage-ultherapy" },
        { label: "HIFU Skin Tightening", href: "/hifu" },
        { label: "PDO Thread Lift", href: "/pdo-threads" },
        { label: "Facial Balancing", href: "/facial-balancing" },
      ]} />
    </ServicePageShell>
  );
}

const AREA_ROWS: { label: string; key: keyof typeof P.perSyringe; note: string }[] = [
  { label: "Lips", key: "lip", note: "Shape, border, and proportion to the lower face" },
  { label: "Cheeks", key: "cheek", note: "Midface support" },
  { label: "Chin", key: "chin", note: "Projection and profile" },
  { label: "Jawline", key: "jawline", note: "Definition along the lower face" },
  { label: "Temples", key: "temple", note: "Hollowing at the sides of the forehead" },
  { label: "Under-eye", key: "underEye", note: "Tear-trough hollows" },
  { label: "Nose (non-surgical)", key: "nose", note: "Filler adds contour; it does not make a nose smaller" },
];

export function FacialBalancing() {
  useEffect(() => { trackViewContent("facial-balancing-dallas"); }, []);
  const pairs = LP_IMAGES.compare.filter((c) => c.key === "platinum-prf" || c.key === "pinktop-front");
  return (
    <ServicePageShell stickyLabel="Facial Balancing" stickyPrice={`Lips ${usd(P.perSyringe.lip)} · Cheek, chin & jaw ${usd(P.perSyringe.cheek)} per syringe`}>
      <PageHero
        eyebrow="Facial balancing in Dallas"
        title="Facial Balancing in Dallas, TX"
        intro="Facial balancing plans the lips, cheeks, chin, jawline, temples, and under-eyes together so the face looks proportionate, rather than treating each area on its own. Dr. Rajan Kohli, MD maps and performs every plan at Lakewood Med Spa, and you see the price before treatment."
        priceSummary={<>Hyaluronic acid filler, per syringe: lips {usd(P.perSyringe.lip)} · cheeks, chin, jawline, temples {usd(P.perSyringe.cheek)} · under-eye or nose {usd(P.perSyringe.underEye)}. Your plan is priced before treatment.</>}
      />
      <div className="container" style={{ maxWidth: 980 }}>
        <ArticleSection title="What facial balancing means">
          <p>Most people do not dislike one feature. They notice that something is out of proportion: a lower face that looks soft next to the cheeks, a chin that sits back, lips that no longer match the rest of the face. Balancing treats those relationships. Sometimes that means a small amount in two places, sometimes a staged plan across several.</p>
          <p>The aim is proportion, not volume everywhere. Dr. Kohli decides what to leave alone as carefully as what to treat, and tells you honestly when a smaller plan is the better one.</p>
        </ArticleSection>

        <ArticleSection title="Areas and prices">
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(220px,1fr))", gap: ".9rem", marginTop: ".6rem" }}>
            {AREA_ROWS.map((a) => (
              <div key={a.key} style={{ background: C.white, border: `1px solid ${C.border}`, borderRadius: 12, padding: "1rem 1.1rem" }}>
                <p style={{ color: C.dark, fontFamily: "'Playfair Display',serif", fontSize: "1.1rem", fontWeight: 700, margin: "0 0 .2rem" }}>{a.label}</p>
                <p style={{ color: C.accent, fontWeight: 800, margin: "0 0 .35rem" }}>{usd(P.perSyringe[a.key])} per syringe</p>
                <p style={{ color: C.muted, fontSize: ".88rem", margin: 0 }}>{a.note}</p>
              </div>
            ))}
          </div>
          <p style={{ marginTop: "1rem" }}>In a multi-syringe plan the first syringe is charged at its area price and each additional syringe is {usd(P.plan.additionalSyringe)}. The plan total is never more than pricing each syringe separately. Collagen biostimulators such as Sculptra or Radiesse can be added and are priced at your consultation.</p>
        </ArticleSection>

        {pairs.length > 0 && (
          <ArticleSection title="Patients treated at Lakewood Med Spa">
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(300px,1fr))", gap: "1.2rem", marginTop: ".6rem" }}>
              {pairs.map((c) => (
                <figure key={c.key} style={{ margin: 0 }}>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: ".4rem" }}>
                    <img src={c.before.src} srcSet={c.before.srcSet} sizes="(min-width: 700px) 240px, 45vw" alt={c.before.alt} width={c.before.width} height={c.before.height} loading="lazy" decoding="async" style={{ width: "100%", height: "auto", borderRadius: 10 }} />
                    <img src={c.after.src} srcSet={c.after.srcSet} sizes="(min-width: 700px) 240px, 45vw" alt={c.after.alt} width={c.after.width} height={c.after.height} loading="lazy" decoding="async" style={{ width: "100%", height: "auto", borderRadius: 10 }} />
                  </div>
                  <figcaption style={{ color: C.muted, fontSize: ".85rem", marginTop: ".5rem" }}>Before (left) and after (right). {c.caption}.</figcaption>
                </figure>
              ))}
            </div>
            <p style={{ fontSize: ".82rem", marginTop: "1rem" }}>Photos are of Lakewood Med Spa patients, shared with their consent. Individual results vary and are not guaranteed.</p>
          </ArticleSection>
        )}

        <ArticleSection title="How it works">
          <ol style={{ margin: 0, paddingLeft: "1.2rem", display: "grid", gap: ".7rem" }}>
            <li><strong>Complimentary consultation.</strong> Dr. Kohli reviews your goals and examines your face. There is no charge.</li>
            <li><strong>A mapped, priced plan.</strong> You see which areas, in what order, and what each costs, before any treatment.</li>
            <li><strong>Treatment by Dr. Kohli.</strong> He performs the treatment himself.</li>
          </ol>
          <p style={{ marginTop: "1rem" }}>Balancing is one of several non-surgical options. If skin looseness is your main concern, see <Link href="/non-surgical-face-lift">non-surgical face lift options</Link>, <Link href="/xerf-skin-tightening">XERF skin tightening</Link>, and <Link href="/hifu">HIFU</Link>.</p>
        </ArticleSection>

        <FaqSection label="Facial balancing frequently asked questions" heading="Facial balancing in Dallas" faqs={FACIAL_BALANCING_FAQS} />
        <div style={{ margin: "0 0 2.5rem" }}><TrademarkNote brands={["Sculptra"]} /></div>
      </div>
      <RelatedLinks links={[
        { label: "Dermal Fillers", href: "/dermal-fillers" },
        { label: "Lip Fillers", href: "/lip-fillers" },
        { label: "Sculptra & Radiesse", href: "/biostimulators" },
        { label: "Non-Surgical Face Lift", href: "/non-surgical-face-lift" },
      ]} />
    </ServicePageShell>
  );
}
